// Shared inbox message classifier. Lives in common/ (not ui/) so the worker can
// reuse the exact same "is this a decision?" rule when it derives the mobile
// red-dot badge state — the worker build blacklists any import from /ui/, and
// duplicating the rule would let the two copies drift. The UI's messageDisplay.ts
// re-exports these for its own consumers.

export type InboxMessageDisplayInput = {
	from?: string;
	mid: number;
	read?: boolean;
	subject?: string;
	text?: string;
	year?: number;
};

export type InboxMessageCategory = "task" | "unread" | "info" | "completed";

const taskPattern =
	/\b(contract|draft|fired|luxury|negotiate|owner|protect|re-sign|resign|trade)\b/i;
const completedPattern =
	/\b(award|champion|completed|congratulations|retired|won)\b/i;

// Owner letters that are informational, never a decision "task". Without this,
// the "The Owner" sender alone trips taskPattern (via "owner") and mis-routes
// them into the must-decide queue — which is also the source of truth for the
// N4-4 red-dot badge, so a clean queue keeps the badge honest.
// - N4-3 in-season feedback letters (monthly pulse, playoff clinch / elimination
//   milestones, free-agency recap).
// - N4-0 season "contract" letter ("Season expectations") and the year-end
//   "Annual performance evaluation" — both are informational reports, not asks.
// Matched by exact stored (English) subject; the UI reverse-translates for
// display so the stored subject stays English.
const informationalOwnerSubjects = new Set([
	"Season update",
	"Playoff berth clinched",
	"Eliminated from playoff contention",
	"Free agency recap",
	"Season expectations",
	"Annual performance evaluation",
]);

const messageSearchText = ({
	from,
	subject,
	text,
}: Pick<InboxMessageDisplayInput, "from" | "subject" | "text">) =>
	`${from ?? ""} ${subject ?? ""} ${text ?? ""}`;

export const classifyInboxMessage = (
	message: InboxMessageDisplayInput,
): InboxMessageCategory => {
	const searchText = messageSearchText(message);

	if (
		message.subject !== undefined &&
		informationalOwnerSubjects.has(message.subject)
	) {
		if (!message.read) {
			return "unread";
		}
		return completedPattern.test(searchText) ? "completed" : "info";
	}

	if (!message.read && taskPattern.test(searchText)) {
		return "task";
	}

	if (!message.read) {
		return "unread";
	}

	if (completedPattern.test(searchText)) {
		return "completed";
	}

	return "info";
};

export { messageSearchText };
